import React, { useState, useEffect, useRef } from 'react';
import { Lead } from '@/types/lead';
import { cn } from '@/lib/utils';
import { Loader2, Check, X, Edit2 } from 'lucide-react';

type EditableCellType = 'text' | 'select' | 'date' | 'number';

interface EditableCellProps {
  value: string | number | null | undefined;
  leadId: string;
  field: keyof Lead;
  onSave: (leadId: string, field: keyof Lead, value: any) => Promise<void>;
  type?: EditableCellType;
  options?: Array<{ value: string; label: string }>;
  className?: string;
  displayValue?: string | React.ReactNode; // Custom display value (e.g., formatted currency or React element)
}

export const EditableCell: React.FC<EditableCellProps> = ({
  value,
  leadId,
  field,
  onSave,
  type = 'text',
  options = [],
  className,
  displayValue,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement>(null);

  // Initialize edit value when entering edit mode
  useEffect(() => {
    if (isEditing) {
      setEditValue(value?.toString() || '');
      // Focus input after render
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isEditing, value]);

  // Handle click outside to cancel editing
  useEffect(() => {
    if (!isEditing) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (inputRef.current && !inputRef.current.contains(e.target as Node)) {
        handleCancel();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isEditing]);

  const handleSave = async () => {
    // Don't save if value hasn't changed
    if (editValue === value?.toString()) {
      setIsEditing(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Convert value based on type
      let parsedValue: any = editValue;

      if (type === 'number') {
        parsedValue = editValue ? parseFloat(editValue) : null;
        if (isNaN(parsedValue)) {
          throw new Error('Invalid number');
        }
      } else if (type === 'date') {
        parsedValue = editValue || null;
      }

      await onSave(leadId, field, parsedValue);
      setIsEditing(false);
    } catch (err) {
      console.error('Failed to save cell:', err);
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setEditValue(value?.toString() || '');
    setIsEditing(false);
    setError(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  // Display mode
  if (!isEditing) {
    return (
      <div
        onClick={() => setIsEditing(true)}
        className={cn(
          'group relative px-3 py-2 cursor-pointer rounded hover:bg-gray-50 transition-colors',
          className
        )}
      >
        <div className="flex items-center justify-between gap-2">
          <span className={cn(!value && 'text-gray-400 italic')}>
            {displayValue || value || 'Click to edit'}
          </span>
          <Edit2 className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>
    );
  }

  // Edit mode
  return (
    <div className={cn('relative', className)}>
      <div className="flex items-center gap-1">
        {type === 'select' ? (
          <select
            ref={inputRef as React.RefObject<HTMLSelectElement>}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            className={cn(
              'flex-1 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500',
              error && 'border-red-500'
            )}
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type={type}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            className={cn(
              'flex-1 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500',
              error && 'border-red-500'
            )}
          />
        )}

        {/* Action buttons */}
        <div className="flex items-center gap-1">
          {isLoading ? (
            <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
          ) : (
            <>
              <button
                onClick={handleSave}
                disabled={isLoading}
                className="p-1 rounded hover:bg-green-100 text-green-600 transition-colors"
                title="Save (Enter)"
              >
                <Check className="w-4 h-4" />
              </button>
              <button
                onClick={handleCancel}
                disabled={isLoading}
                className="p-1 rounded hover:bg-red-100 text-red-600 transition-colors"
                title="Cancel (Esc)"
              >
                <X className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="absolute top-full left-0 mt-1 px-2 py-1 text-xs text-red-600 bg-red-50 rounded shadow-sm whitespace-nowrap z-10">
          {error}
        </div>
      )}
    </div>
  );
};
